import { useEffect } from "react";

const useBlockInteraction = (isBlocked: boolean) => {
  useEffect(() => {
    if (!isBlocked) return;

    // ─── Track touch movement to differentiate tap vs scroll ──
    let touchMoved = false;
    let touchStartX = 0;
    let touchStartY = 0;
    // Threshold in pixels to consider it a scroll not a tap
    const MOVE_THRESHOLD = 5;

    const blockEvent = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    const blockFocus = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const target = e.target as HTMLElement;
      if (target && typeof target.blur === "function") {
        target.blur();
      }
    };

    // ─── Touch Start: Record initial position ──────────────
    const handleTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      touchMoved = false; // Reset on each new touch
      touchStartX = touchEvent.touches[0].clientX;
      touchStartY = touchEvent.touches[0].clientY;
      // Don't block touchstart - needed for scroll to work
    };

    // ─── Touch Move: Mark as scroll if moved enough ─────────
    const handleTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      const deltaX = Math.abs(touchEvent.touches[0].clientX - touchStartX);
      const deltaY = Math.abs(touchEvent.touches[0].clientY - touchStartY);

      if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
        touchMoved = true; //  It's a scroll, not a tap
      }
      // Don't block touchmove - needed for scroll to work
    };

    // ─── Touch End: Block only if it was a tap not scroll ───
    const handleTouchEnd = (e: Event) => {
      if (!touchMoved) {
        // Finger didn't move = it's a TAP = BLOCK IT
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
      //  If touchMoved = true, it's a scroll = ALLOW IT
      touchMoved = false; // Reset for next touch
    };

    const eventOptions: AddEventListenerOptions = {
      capture: true,
      passive: false,
    };

    const cleanupOptions: EventListenerOptions = {
      capture: true,
    };

    // ─── Mouse Events (Desktop/Laptop) ─────────────────────
    const mouseEvents: (keyof DocumentEventMap)[] = [
      "click",
      "dblclick",
      "mousedown",
      "mouseup",
      "auxclick",
      "contextmenu",
    ];

    // ─── Pointer Events (Stylus/Pen) ───────────────────────
    const pointerEvents: (keyof DocumentEventMap)[] = [
      "pointerdown",
      "pointerup",
      "pointercancel",
    ];

    // ─── Keyboard Events ───────────────────────────────────
    const keyboardEvents: (keyof DocumentEventMap)[] = [
      "keydown",
      "keyup",
      "keypress",
    ];

    // ─── Focus Events ──────────────────────────────────────
    const focusEvents: (keyof DocumentEventMap)[] = ["focus", "focusin"];

    // ─── Register Mouse/Pointer/Keyboard/Focus Events ──────
    mouseEvents.forEach((event) => {
      document.addEventListener(event, blockEvent, eventOptions);
    });

    pointerEvents.forEach((event) => {
      document.addEventListener(event, blockEvent, eventOptions);
    });

    keyboardEvents.forEach((event) => {
      document.addEventListener(event, blockEvent, eventOptions);
    });

    focusEvents.forEach((event) => {
      document.addEventListener(event, blockFocus, eventOptions);
    });

    // ─── Register Touch Events Separately ──────────────────
    // passive: false needed to allow preventDefault in handlers
    document.addEventListener("touchstart", handleTouchStart, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchmove", handleTouchMove, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchcancel", handleTouchEnd, {
      capture: true,
      passive: false,
    });

    // ─── Cleanup ───────────────────────────────────────────
    return () => {
      mouseEvents.forEach((event) => {
        document.removeEventListener(event, blockEvent, cleanupOptions);
      });

      pointerEvents.forEach((event) => {
        document.removeEventListener(event, blockEvent, cleanupOptions);
      });

      keyboardEvents.forEach((event) => {
        document.removeEventListener(event, blockEvent, cleanupOptions);
      });

      focusEvents.forEach((event) => {
        document.removeEventListener(event, blockFocus, cleanupOptions);
      });

      document.removeEventListener(
        "touchstart",
        handleTouchStart,
        cleanupOptions,
      );
      document.removeEventListener(
        "touchmove",
        handleTouchMove,
        cleanupOptions,
      );
      document.removeEventListener("touchend", handleTouchEnd, cleanupOptions);
      document.removeEventListener(
        "touchcancel",
        handleTouchEnd,
        cleanupOptions,
      );
    };
  }, [isBlocked]);
};

export default useBlockInteraction;
